import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Typography } from 'glintly-ui';
import { ArrowLeft, Star, MapPin, Clock, User } from 'lucide-react';
import { useVerifiedShops } from '../../api';
import { useDispatch, useSelector } from 'react-redux';
import { addToCart, updateQuantity, removeFromCart } from '../../redux/slice/cartSlice';
import styles from './services.module.css';

const SalonServices = () => {
  const { salonId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: salons = [], isLoading, error } = useVerifiedShops();
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Get cart state
  const cartItems = useSelector((state) => state.cart.items);

  // Find the specific salon
  const salon = salons.find((s) => s._id === salonId);

  const handleBackToSalons = () => {
    navigate('/services');
  };

  const handleServiceClick = (serviceId) => {
    navigate(`/services/${serviceId}`);
  };

  // Helper function to check if item is in cart
  const isItemInCart = (productId) => {
    return cartItems.some((item) => item.productId === productId);
  };

  // Helper function to get cart item
  const getCartItem = (productId) => {
    return cartItems.find((item) => item.productId === productId);
  };

  const handleAddToCart = (service, e) => {
    e.stopPropagation();
    dispatch(
      addToCart({
        productId: service._id,
        shopId: service.shop,
        name: service.name,
        price: service.price,
        image: service.images && service.images[0],
        quantity: 1,
        variants: service.variants || {},
      })
    );
  };

  const handleQuantityChange = (productId, variants, change) => {
    const cartItem = getCartItem(productId);
    if (!cartItem) return;

    const newQuantity = cartItem.quantity + change;

    if (newQuantity <= 0) {
      dispatch(removeFromCart({ productId, variants }));
    } else {
      dispatch(updateQuantity({ productId, quantity: newQuantity, variants }));
    }
  };

  // Get unique categories from salon's services
  const categories = salon?.products
    ? ['all', ...new Set(salon.products.map((service) => service.category).filter(Boolean))]
    : ['all'];

  // Filter services by category
  const filteredServices =
    salon?.products?.filter((service) => {
      if (selectedCategory === 'all') return true;
      return service.category === selectedCategory;
    }) || [];

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p className={styles.loadingText}>Loading salon details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorIcon}>⚠️</div>
        <h3 className={styles.errorTitle}>Oops! Something went wrong</h3>
        <p className={styles.errorMessage}>{error}</p>
        <Button onClick={() => navigate('/services')} className={styles.retryButton}>
          Back
        </Button>
      </div>
    );
  }

  if (!salon) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorIcon}>🏪</div>
        <h3 className={styles.errorTitle}>Salon not found</h3>
        <p className={styles.errorMessage}>The salon you're looking for doesn't exist.</p>
        <Button onClick={() => navigate('/services')} className={styles.retryButton}>
          Back
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.salonServicesContainer}>
      {/* Header */}
      <div className={styles.salonServicesHeader}>
        <button className={styles.backButton} onClick={handleBackToSalons}>
          <ArrowLeft className={styles.backIcon} />
          Back
        </button>
      </div>

      {/* Salon Info */}
      <div className={styles.salonInfoCard}>
        <div className={styles.salonImageContainer}>
          {salon.images && salon.images[0] ? (
            <img src={salon.images[0]} alt={salon.name} className={styles.salonHeroImage} />
          ) : (
            <div className={styles.placeholderImage}>
              <span className={styles.placeholderIcon}>🏪</span>
            </div>
          )}
        </div>

        <div className={styles.salonInfoContent}>
          <div className={styles.salonInfoHeader}>
            <Typography className={styles.salonInfoTitle}>{salon.name}</Typography>
            <div className={styles.salonRating}>
              <Star className={styles.starIcon} />
              <span className={styles.ratingValue}>{salon.rating || 'N/A'}</span>
            </div>
          </div>

          {salon.address && (
            <div className={styles.salonLocation}>
              <MapPin className={styles.locationIcon} />
              <span className={styles.locationText}>
                {salon.address.fullAddress ||
                  `${salon.address.street || ''} ${salon.address.city || ''} ${salon.address.state || ''} ${salon.address.postalCode || ''}`.trim()}
              </span>
            </div>
          )}

          {salon.description && <p className={styles.salonDescription}>{salon.description}</p>}

          <div className={styles.salonStats}>
            <div className={styles.statItem}>
              <User className={styles.statIcon} />
              <span className={styles.statText}>Verified Salon</span>
            </div>
            <div className={styles.statItem}>
              <Clock className={styles.statIcon} />
              <span className={styles.statText}>{salon.products?.length || 0} Services</span>
            </div>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className={styles.servicesSection}>
        {/* Category Filter */}
        {categories.length > 1 && (
          <div className={styles.categoryFilter}>
            <div className={styles.categoryButtons}>
              {categories.map((category) => (
                <button
                  key={category}
                  className={`${styles.categoryButton} ${
                    selectedCategory === category ? styles.activeCategory : ''
                  }`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category === 'all' ? 'All Services' : category}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Services Grid */}
        {filteredServices.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>💅</div>
            <h3 className={styles.emptyTitle}>No services found</h3>
            <p className={styles.emptyMessage}>
              This salon doesn't have any services in this category.
            </p>
          </div>
        ) : (
          <div className={styles.servicesGrid}>
            {filteredServices.map((service) => (
              <div
                key={service._id}
                className={styles.serviceCard}
                onClick={() => handleServiceClick(service._id)}
              >
                <div className={styles.serviceImageContainer}>
                  {service.images && service.images[0] ? (
                    <img
                      src={service.images[0]}
                      alt={service.name}
                      className={styles.serviceImage}
                    />
                  ) : (
                    <div className={styles.placeholderImage}>
                      <span className={styles.placeholderIcon}>💅</span>
                    </div>
                  )}
                  <div className={styles.serviceOverlay}>
                    <span className={styles.viewDetailsText}>View Details</span>
                  </div>
                </div>

                <div className={styles.serviceContent}>
                  <div className={styles.serviceHeader}>
                    <h3 className={styles.serviceName}>{service.name}</h3>
                    <span className={styles.servicePrice}>₹{service.price}</span>
                  </div>

                  {service.description && (
                    <p className={styles.serviceDescription}>{service.description}</p>
                  )}

                  {service.category && (
                    <span className={styles.serviceCategory}>{service.category}</span>
                  )}

                  {service.duration && (
                    <div className={styles.serviceDuration}>
                      <Clock className={styles.durationIcon} />
                      <span className={styles.durationText}>{service.duration}</span>
                    </div>
                  )}
                  {isItemInCart(service._id) ? (
                    <div className={styles.quantityControls}>
                      <button
                        className={styles.quantityBtn}
                        onClick={() => handleQuantityChange(service._id, service.variants, -1)}
                        disabled={getCartItem(service._id)?.quantity <= 1}
                      >
                        −
                      </button>
                      <span className={styles.quantityValue}>
                        {getCartItem(service._id)?.quantity || 0}
                      </span>
                      <button
                        className={styles.quantityBtn}
                        onClick={() => handleQuantityChange(service._id, service.variants, 1)}
                      >
                        +
                      </button>
                    </div>
                  ) : (
                    <Button
                      className={styles.addToCartBtn}
                      onClick={(e) => handleAddToCart(service, e)}
                    >
                      Add to Cart
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SalonServices;
